import React, { useEffect, useRef, useState } from "react";
import { CANVAS_W, CANVAS_H, fontCss } from "./canvasModel";
import { SingleWidget } from "./WidgetOverlay";

/**
 * CanvasRenderer renders an array of elements inside a fixed 1920x1080
 * canvas which auto-scales to fit its parent (preserves aspect ratio).
 *
 * Used by:
 *   - MenuEditor preview pane (via CanvasEditor)
 *   - TV display (when design.config.elements exists)
 *   - Templates page miniature previews (via MenuRenderer fallback)
 */
export function CanvasRenderer({ elements = [], background = "#FFFFFF", scaleToFit = true, hideIds = [], canvasW = CANVAS_W, canvasH = CANVAS_H }) {
  const wrapRef = useRef(null);
  const [scale, setScale] = useState(1);

  useEffect(() => {
    if (!scaleToFit) return;
    const compute = () => {
      const w = wrapRef.current?.clientWidth || canvasW;
      const h = wrapRef.current?.clientHeight || canvasH;
      setScale(Math.min(w / canvasW, h / canvasH));
    };
    compute();
    const ro = new ResizeObserver(compute);
    if (wrapRef.current) ro.observe(wrapRef.current);
    return () => ro.disconnect();
  }, [scaleToFit, canvasW, canvasH]);

  // Sort by z, then filter out any element being edited live (so it doesn't
  // double-render under the editing textarea).
  const hideSet = new Set(hideIds || []);
  const ordered = [...elements]
    .filter((el) => !hideSet.has(el.id))
    .sort((a, b) => (a.z ?? 0) - (b.z ?? 0));

  return (
    <div ref={wrapRef} className="relative w-full h-full overflow-hidden" style={{ background }}>
      <div
        className="absolute"
        style={{
          width: canvasW,
          height: canvasH,
          left: scaleToFit ? "50%" : 0,
          top:  scaleToFit ? "50%" : 0,
          transform: scaleToFit ? `translate(-50%, -50%) scale(${scale})` : "none",
          transformOrigin: "center center",
        }}
      >
        {ordered.map((el) => (
          <ElementRender key={el.id} el={el} />
        ))}
      </div>
    </div>
  );
}

function ElementRender({ el }) {
  const transform = [
    el.rotation ? `rotate(${el.rotation}deg)` : null,
    el.skewX ? `skewX(${el.skewX}deg)` : null,
  ].filter(Boolean).join(" ") || undefined;
  const style = {
    position: "absolute",
    left: el.x,
    top: el.y,
    width: el.w,
    height: el.h,
    transform,
    transformOrigin: "center center",
    opacity: el.opacity ?? 1,
  };
  if (el.kind === "text") {
    const stroke = el.textStrokeWidth ? `${el.textStrokeWidth}px ${el.textStrokeColor || "#FFFFFF"}` : undefined;
    return (
      <div
        style={{
          ...style,
          color: el.color,
          background: el.bgColor || "transparent",
          borderRadius: el.bgRadius || 0,
          fontFamily: fontCss(el.fontFamily),
          fontSize: el.fontSize,
          fontWeight: el.fontWeight ?? 400,
          fontStyle: el.italic ? "italic" : "normal",
          textDecoration: [el.underline && "underline", el.strike && "line-through"].filter(Boolean).join(" ") || "none",
          textAlign: el.align || "left",
          lineHeight: el.lineHeight ?? 1.2,
          letterSpacing: el.letterSpacing ?? 0,
          textShadow: el.textShadow || undefined,
          WebkitTextStroke: stroke,
          display: "flex",
          alignItems: el.valign === "top" ? "flex-start" : el.valign === "bottom" ? "flex-end" : "center",
          justifyContent: el.align === "right" ? "flex-end" : el.align === "center" ? "center" : "flex-start",
          padding: el.padding || 0,
          whiteSpace: "pre-wrap",
          overflow: "hidden",
        }}
      >
        <span style={{ width: "100%" }}>{el.text}</span>
      </div>
    );
  }
  if (el.kind === "image") {
    return (
      <div style={{ ...style, overflow: "hidden", borderRadius: el.borderRadius || 0, filter: el.dropShadow || undefined }}>
        {el.src
          ? <img src={el.src} alt="" draggable={false} style={{ width: "100%", height: "100%", objectFit: el.fit || "cover", display: "block" }} />
          : <div style={{ width: "100%", height: "100%", background: "#E2E8F0", display: "flex", alignItems: "center", justifyContent: "center", color: "#64748B", fontSize: 22 }}>No image</div>}
      </div>
    );
  }
  // Video element (e.g. an MP4 exported from a Canva video design). Rendered
  // as a native <video> so playback works everywhere — Chrome, Safari, and
  // the Android WebView the Nexso stick uses. `muted` + `playsInline` are
  // required for autoplay on iOS Safari and on some Android WebView builds;
  // without them the video stays frozen on its first frame (which is the
  // exact bug the customer reported for Canva video imports).
  if (el.kind === "video") {
    return (
      <div style={{ ...style, overflow: "hidden", borderRadius: el.borderRadius || 0, background: "#000" }}>
        {el.src ? (
          <video
            src={el.src}
            poster={el.poster || undefined}
            autoPlay={el.autoplay !== false}
            muted={el.muted !== false}
            loop={el.loop !== false}
            playsInline={el.playsInline !== false}
            controls={el.controls === true}
            preload="auto"
            style={{ width: "100%", height: "100%", objectFit: el.fit || "cover", display: "block" }}
          />
        ) : (
          <div style={{ width: "100%", height: "100%", background: "#0F172A", display: "flex", alignItems: "center", justifyContent: "center", color: "#94A3B8", fontSize: 22 }}>No video</div>
        )}
      </div>
    );
  }
  if (el.kind === "shape") {
    return renderShape(el, style);
  }
  if (el.kind === "widget") {
    return (
      <div style={{ ...style, borderRadius: 16, overflow: "hidden" }} data-testid={`widget-${el.widgetType}`}>
        <SingleWidget type={el.widgetType} config={el.widgetConfig} />
      </div>
    );
  }
  return null;
}

// ----------------------------------------------------------------------------
// Shape renderer — supports 16 named shapes.
//
// Simple polygons (triangle, diamond, hex, pentagon, star, arrows, chevrons,
// plus/cross, trapezoid, parallelogram, sunburst) use CSS `clip-path` so a
// single <div> handles the fill + stretch. Curved shapes (heart, speech
// bubble) fall through to an inline SVG so their curves stay smooth at any
// size.
//
// The stretch/fill respects the element's box, so the SAME shape can be
// dragged into any aspect ratio — a wide rectangle → a wide triangle etc.
// ----------------------------------------------------------------------------
function renderShape(el, style) {
  const stroke = el.strokeWidth
    ? `${el.strokeWidth}px solid ${el.stroke || "#0F172A"}`
    : undefined;
  const shape = el.shape || "rect";
  const common = { ...style, background: el.fill || "#0EA5E9", border: stroke };

  if (shape === "rect") {
    return <div style={{ ...common, borderRadius: el.borderRadius || 0 }} />;
  }
  if (shape === "circle" || shape === "ellipse") {
    return <div style={{ ...common, borderRadius: "50%" }} />;
  }

  // Curved shapes → inline SVG (require path/circle primitives).
  if (shape === "heart") {
    return (
      <div style={style}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
          <path
            d="M50 88 C 20 68, 5 45, 15 25 C 22 12, 40 12, 50 28 C 60 12, 78 12, 85 25 C 95 45, 80 68, 50 88 Z"
            fill={el.fill || "#EF4444"}
            stroke={el.strokeWidth ? (el.stroke || "#0F172A") : "none"}
            strokeWidth={el.strokeWidth || 0}
          />
        </svg>
      </div>
    );
  }
  if (shape === "speech") {
    return (
      <div style={style}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
          <path
            d="M8 8 H92 A8 8 0 0 1 100 16 V70 A8 8 0 0 1 92 78 H40 L22 96 L28 78 H8 A8 8 0 0 1 0 70 V16 A8 8 0 0 1 8 8 Z"
            fill={el.fill || "#0EA5E9"}
            stroke={el.strokeWidth ? (el.stroke || "#0F172A") : "none"}
            strokeWidth={el.strokeWidth || 0}
          />
        </svg>
      </div>
    );
  }
  // Cloud — 4 puffs, playful and instantly recognisable at any size.
  if (shape === "cloud") {
    return (
      <div style={style}>
        <svg viewBox="0 0 120 60" preserveAspectRatio="none" width="100%" height="100%">
          <path
            d="M30 55 C 12 55, 5 40, 15 30 C 15 15, 35 8, 45 18 C 50 5, 75 5, 82 20 C 105 15, 120 35, 105 50 Z"
            fill={el.fill || "#0EA5E9"}
            stroke={el.strokeWidth ? (el.stroke || "#0F172A") : "none"}
            strokeWidth={el.strokeWidth || 0}
          />
        </svg>
      </div>
    );
  }
  // Semi-circles are drawn as an ellipse clipped by a rect — clip-path can't
  // do smooth curves, so SVG paths with A (arc) commands work perfectly.
  if (shape === "semicircle-top" || shape === "semicircle-bottom" || shape === "semicircle-left" || shape === "semicircle-right") {
    const paths = {
      "semicircle-top":    "M 0 100 A 50 100 0 0 1 100 100 Z",
      "semicircle-bottom": "M 0 0 A 50 100 0 0 0 100 0 Z",
      "semicircle-left":   "M 100 0 A 100 50 0 0 0 100 100 Z",
      "semicircle-right":  "M 0 0 A 100 50 0 0 1 0 100 Z",
    };
    return (
      <div style={style}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
          <path d={paths[shape]}
            fill={el.fill || "#0EA5E9"}
            stroke={el.strokeWidth ? (el.stroke || "#0F172A") : "none"}
            strokeWidth={el.strokeWidth || 0}
          />
        </svg>
      </div>
    );
  }
  if (shape === "quarter-circle") {
    return (
      <div style={style}>
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" width="100%" height="100%">
          <path d="M 0 100 L 0 0 A 100 100 0 0 1 100 100 Z"
            fill={el.fill || "#0EA5E9"}
            stroke={el.strokeWidth ? (el.stroke || "#0F172A") : "none"}
            strokeWidth={el.strokeWidth || 0}
          />
        </svg>
      </div>
    );
  }
  // Rounded rectangle — dedicated shape so users can pick a nice pill without
  // fiddling with the numeric borderRadius input.
  if (shape === "rounded-rect") {
    return <div style={{ ...common, borderRadius: "12%" }} />;
  }

  // Polygonal shapes → CSS clip-path (single scaling div).
  const clip = CLIP_PATHS[shape];
  if (clip) {
    return <div style={{ ...common, clipPath: clip, WebkitClipPath: clip }} />;
  }
  // Unknown shape — degrade to rect so we never render nothing.
  return <div style={{ ...common, borderRadius: el.borderRadius || 0 }} />;
}

const CLIP_PATHS = {
  triangle:      "polygon(50% 0%, 100% 100%, 0% 100%)",
  "right-triangle": "polygon(0% 0%, 0% 100%, 100% 100%)",
  diamond:       "polygon(50% 0%, 100% 50%, 50% 100%, 0% 50%)",
  hexagon:       "polygon(25% 0%, 75% 0%, 100% 50%, 75% 100%, 25% 100%, 0% 50%)",
  "hexagon-v":   "polygon(50% 0%, 100% 25%, 100% 75%, 50% 100%, 0% 75%, 0% 25%)",
  pentagon:      "polygon(50% 0%, 100% 38%, 82% 100%, 18% 100%, 0% 38%)",
  octagon:       "polygon(30% 0%, 70% 0%, 100% 30%, 100% 70%, 70% 100%, 30% 100%, 0% 70%, 0% 30%)",
  star:          "polygon(50% 0%, 61% 35%, 98% 35%, 68% 57%, 79% 91%, 50% 70%, 21% 91%, 32% 57%, 2% 35%, 39% 35%)",
  star4:         "polygon(50% 0%, 60% 40%, 100% 50%, 60% 60%, 50% 100%, 40% 60%, 0% 50%, 40% 40%)",
  star6:         "polygon(50% 0%, 62% 34%, 100% 34%, 68% 55%, 82% 100%, 50% 74%, 18% 100%, 32% 55%, 0% 34%, 38% 34%)",
  star8:         "polygon(50% 0%, 58% 20%, 79% 15%, 76% 36%, 100% 50%, 76% 64%, 79% 85%, 58% 80%, 50% 100%, 42% 80%, 21% 85%, 24% 64%, 0% 50%, 24% 36%, 21% 15%, 42% 20%)",
  burst:         "polygon(50% 0%, 58% 15%, 75% 5%, 75% 25%, 95% 25%, 82% 40%, 100% 50%, 82% 60%, 95% 75%, 75% 75%, 75% 95%, 58% 85%, 50% 100%, 42% 85%, 25% 95%, 25% 75%, 5% 75%, 18% 60%, 0% 50%, 18% 40%, 5% 25%, 25% 25%, 25% 5%, 42% 15%)",
  "arrow-right": "polygon(0% 25%, 60% 25%, 60% 0%, 100% 50%, 60% 100%, 60% 75%, 0% 75%)",
  "arrow-left":  "polygon(40% 0%, 40% 25%, 100% 25%, 100% 75%, 40% 75%, 40% 100%, 0% 50%)",
  "arrow-up":    "polygon(50% 0%, 100% 40%, 75% 40%, 75% 100%, 25% 100%, 25% 40%, 0% 40%)",
  "arrow-down":  "polygon(25% 0%, 75% 0%, 75% 60%, 100% 60%, 50% 100%, 0% 60%, 25% 60%)",
  "chevron-right":"polygon(0% 0%, 65% 0%, 100% 50%, 65% 100%, 0% 100%, 35% 50%)",
  "chevron-left":"polygon(35% 0%, 100% 0%, 65% 50%, 100% 100%, 35% 100%, 0% 50%)",
  "chevron-up":  "polygon(50% 0%, 100% 35%, 100% 65%, 50% 30%, 0% 65%, 0% 35%)",
  "chevron-down":"polygon(0% 35%, 0% 65%, 50% 100%, 100% 65%, 100% 35%, 50% 70%)",
  plus:          "polygon(35% 0%, 65% 0%, 65% 35%, 100% 35%, 100% 65%, 65% 65%, 65% 100%, 35% 100%, 35% 65%, 0% 65%, 0% 35%, 35% 35%)",
  cross:         "polygon(35% 0%, 65% 0%, 65% 35%, 100% 35%, 100% 65%, 65% 65%, 65% 100%, 35% 100%, 35% 65%, 0% 65%, 0% 35%, 35% 35%)",
  "cross-x":     "polygon(0% 15%, 15% 0%, 50% 35%, 85% 0%, 100% 15%, 65% 50%, 100% 85%, 85% 100%, 50% 65%, 15% 100%, 0% 85%, 35% 50%)",
  parallelogram: "polygon(20% 0%, 100% 0%, 80% 100%, 0% 100%)",
  trapezoid:     "polygon(20% 0%, 80% 0%, 100% 100%, 0% 100%)",
  // Decorative
  bolt:          "polygon(52% 0%, 15% 55%, 42% 55%, 30% 100%, 85% 40%, 55% 40%, 68% 0%)",
  flag:          "polygon(0% 0%, 100% 0%, 85% 50%, 100% 100%, 0% 100%)",
  bookmark:      "polygon(0% 0%, 100% 0%, 100% 100%, 50% 80%, 0% 100%)",
  tag:           "polygon(0% 50%, 20% 0%, 100% 0%, 100% 100%, 20% 100%)",
  kite:          "polygon(50% 0%, 100% 35%, 50% 100%, 0% 35%)",
};

